<script setup lang="ts">
import { computed, reactive } from 'vue'
import Icon from '@/components/partials/Icon.vue'
import LoadingSpinner from '@/components/partials/Loading/LoadingSpinner.vue'

const props = defineProps({
  label: { type: String, default: null },
  action: { type: String, default: null },
  name: { type: String, default: 'text-input' },
  dataTestId: { type: String, default: 'text-input' },
  actionDataTestId: { type: String, default: null },
  placeholder: { type: String, default: '' },
  error: { type: String, default: null },
  modelValue: { type: String, default: '' },
  type: { type: String, default: 'text' },
  inputmode: { type: String, default: 'text' },
  loading: { bool: Boolean },
  autocomplete: { type: String, default: 'on' },
  autocorrect: { type: String, default: 'on' }, // on | off
  disabled: { bool: Boolean },
  rounded: { bool: Boolean },
})

const emit = defineEmits([ 'actionClick', 'update:modelValue', 'hitEnter', 'input', 'change' ])

const state = reactive({
  visible: false,
})

const value = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
})

const toggledType = computed(() => {
  if (props.type === 'password') return state.visible ? 'text' : 'password'
  return props.type
})

const toggleVisibility = () => {
  state.visible = !state.visible
}
</script>

<template>
  <div class="text-input">
    <div v-if="label || action"
         class="text-input__labels"
    >
      <div v-if="label"
           class="text-input__label"
      >
        {{ label }}
      </div>
      <div v-if="action"
           class="text-input__action-label"
           :data-test-id="actionDataTestId"
           @click="$emit('actionClick')"
      >
        {{ action }}
      </div>
    </div>
    <div class="text-input__input-container">
      <input v-taptic="'selection'"
             class="text-input__input"
             :class="{
          'text-input__input--disabled': disabled,
          'text-input__input--error': error !== null,
          'text-input__input--rounded': $props.rounded,
        }"
             :type="toggledType"
             :inputmode="inputmode"
             :autocomplete="autocomplete"
             :autocorrect="autocorrect"
             :name="name"
             :placeholder="placeholder"
             v-model="value"
             :data-test-id="dataTestId"
             :disabled="disabled"
             @input="emit('input', $event)"
             @change="emit('change', $event)"
             @keyup.enter="(e) => $emit('hitEnter', e.target.value)"
      >
      <div v-if="type === 'password'"
           class="text-input__visibility-toggle"
           @click="toggleVisibility"
      >
        <Icon name="eye-crossed" v-if="toggledType === 'password'"/>
        <Icon name="eye" v-if="toggledType !== 'password'"/>
      </div>
      <div v-if="loading"
           class="text-input__loader"
      >
        <LoadingSpinner :size="16"/>
      </div>
    </div>
    <span v-if="error !== null"
          class="text-input__message"
    > {{ error }} </span>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-down.scss";

.text-input {
  &__labels {
    display:       flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }

  &__action-label {
    color: var(--color-brand-primary);
    cursor: pointer;
  }

  &__input {
    padding:     0.75rem 2rem 0.75rem 1rem;
    margin:      0;
    border:      1px solid var(--color-neutral-quaternary);
    border-radius: 0.5rem;
    width:       100%;
    line-height: 1.4;

    &--disabled {
      background-color: #f7f7f7;
    }

    &--error {
      border-color: #e4a6a6;
    }

    &--rounded {
      border-radius: 2rem;
    }

    @include lg-down {
      padding: 0.75rem 1rem 0.75rem 1rem;
    }
  }

  &__input-container {
    position: relative;
  }

  &__visibility-toggle {
    position: absolute;
    top:      50%;
    right:    0;
    cursor:   pointer;
    padding:  1rem;
    transform: translateY(-50%);
  }

  &__loader {
    position: absolute;
    width:    1rem;
    height:   1rem;
    top:      50%;
    right:    1rem;
    transform: translateY(-50%);
  }

  &__message {
    font-size: 0.9rem;
    color:     #e43e3e;
    display:   block;
    margin-top: 0.5rem;
  }
}
</style>
